import React, { ReactNode, useMemo } from 'react';
import { isEmpty } from 'lodash';
import { SortItem } from './sort-item';
import { ListFilterItem } from './list-filter-item';
import {
  DateRangeState,
  FilterRoute,
  FiltersPayload,
  ListFilterKey,
  ListFiltersMap,
  SortDirection,
  SortOption,
  SortOptionsMap,
  TreeFilterKeys,
  TreeFilterMap,
  TreeInputData,
} from '@zonofi/zono-money-store';
import { DateFilterItem } from './date-filter-item';
import { useUrlDateRange } from '../../hooks';
import { showDateFilter } from '../../helper';
import { TreeFilterItem } from './tree-filter-item';
import { SEARCH_ENABLED_ROUTES } from './defined-filter';
import { RelativeTimeRange } from '../../types';

interface FilterRightSectionProps {
  currentIndex: number;
  filters: FiltersPayload;
  onUpdateFilter: (filter: FiltersPayload) => void;
  dateRangeSelection?: DateRangeState | null;
  setDateRangeSelection?: (val: DateRangeState | null) => void;
  route?: FilterRoute;
  dateSelectionMode?: 'date' | 'dateRange' | undefined;
  localTimeRange: RelativeTimeRange | null;
  setLocalTimeRange: (val: RelativeTimeRange | null) => void;
}

export const FilterRightSection: React.FC<FilterRightSectionProps> = ({
  currentIndex,
  filters,
  onUpdateFilter,
  dateRangeSelection,
  setDateRangeSelection,
  route,
  dateSelectionMode = 'dateRange',
  localTimeRange,
  setLocalTimeRange,
}) => {
  const { sortOptions = {}, listFilter = {}, treeFilter = {} } = filters;
  const { dateRangeFromUrl } = useUrlDateRange({ route });

  const onSortItemPress = (itemId: string, direction: SortDirection) => {
    const updatedSort = Object.fromEntries(
      Object.entries(sortOptions).map(([key, sortValue]) => {
        if (itemId === key) {
          return [key, { ...sortValue, selected: direction } as SortOption];
        } else return [key, { ...sortValue, selected: false } as SortOption];
      })
    ) as SortOptionsMap;
    onUpdateFilter({ sortOptions: updatedSort });
  };

  const onFilterItemPress = (filterKey: ListFilterKey, itemId: string) => {
    const filters = { ...(listFilter?.[filterKey] || {}) };
    if (filters) {
      const { multiSelect = false, values = [] } = filters;
      const updatedData = values.map((x) => {
        if (x.id === itemId) {
          return { ...x, selected: !x.selected };
        } else {
          if (multiSelect) return x;
          else return { ...x, selected: false };
        }
      });
      filters.values = updatedData;
    }
    const updatedFilters: Partial<ListFiltersMap> = {
      ...listFilter,
      [filterKey]: filters,
    };
    onUpdateFilter({ listFilter: updatedFilters });
  };

  const onFilterTreeItemPress = (filterKey: TreeFilterKeys, itemId: string) => {
    const filters = treeFilter?.[filterKey];
    if (!filters?.value) return;

    const updateTree = (value: TreeInputData[]): TreeInputData[] => {
      return value.map((item) => {
        if (item.id === itemId) {
          return { ...item, selected: !item.selected };
        } else if (item.data?.length) {
          return { ...item, data: updateTree(item.data) };
        }
        return item;
      });
    };

    const updatedTree = updateTree(filters.value);
    const updatedTreeFilters: Partial<TreeFilterMap> = {
      ...treeFilter,
      [filterKey]: { ...filters, value: updatedTree },
    };
    onUpdateFilter({ treeFilter: updatedTreeFilters });
  };

  const pages: ReactNode[] = useMemo(() => {
    const filterPages: ReactNode[] = [];

    if (!isEmpty(sortOptions)) {
      filterPages.push(
        <SortItem sortOptions={sortOptions} onItemPress={onSortItemPress} />
      );
    }

    Object.entries(listFilter).forEach(([filterKey, data]) => {
      filterPages.push(
        <ListFilterItem
          filterKey={filterKey as ListFilterKey}
          data={data}
          onItemPress={(id) =>
            onFilterItemPress(filterKey as ListFilterKey, id)
          }
          needSearch={SEARCH_ENABLED_ROUTES[route as FilterRoute]}
        />
      );
    });

    Object.entries(treeFilter).forEach(([filterKey, data]) => {
      filterPages.push(
        <TreeFilterItem
          filterKey={filterKey as TreeFilterKeys}
          data={data}
          onItemPress={(id) =>
            onFilterTreeItemPress(filterKey as TreeFilterKeys, id)
          }
          needSearch={SEARCH_ENABLED_ROUTES[route as FilterRoute]}
        />
      );
    });

    if (
      showDateFilter(route as FilterRoute) &&
      dateRangeFromUrl?.startDate &&
      dateRangeFromUrl?.endDate
    ) {
      filterPages.push(
        <DateFilterItem
          data={dateRangeFromUrl as DateRangeState}
          dateRangeSelection={dateRangeSelection as DateRangeState}
          setDateRangeSelection={
            setDateRangeSelection as (val: DateRangeState | null) => void
          }
          dateSelectionMode={dateSelectionMode}
          route={route}
          localTimeRange={localTimeRange}
          setLocalTimeRange={setLocalTimeRange}
        />
      );
    }

    return filterPages;
  }, [listFilter, sortOptions, dateRangeFromUrl, localTimeRange]);

  return <div className="filter-modal-right-box">{pages[currentIndex]}</div>;
};
